import React, { useCallback, useEffect, useState } from "react";
import {
	blockUser,
	getReports,
	markReportViewed,
	unBlockUser,
} from "../../api/apiServiceFetch";
import { FILE_BASE_URL } from "../../config/env";
import userPng from "./user.png";
import "./Reports.scss";

const formatDate = (value) => {
	if (!value) return "-";

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "-";

	return date.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const Reports = () => {
	const [groupedReports, setGroupedReports] = useState([]);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [processing, setProcessing] = useState({});

	const selectedGroup = selectedUserId
		? groupedReports.find((g) => g.userId === selectedUserId) || null
		: null;
	const selectedUserReports = selectedGroup ? selectedGroup.reports : null;

	const filePath = FILE_BASE_URL;

	const getPhotoSrc = (userId, fileName) => {
		if (!userId || !fileName || fileName.trim().length === 0) {
			return userPng;
		}

		return `${filePath}/Photo/${userId}/${fileName}`;
	};
	const copyToClipboard = async (value, event) => {
		event?.stopPropagation();

		if (!value) return;

		try {
			await navigator.clipboard.writeText(String(value));
		} catch (err) {
			console.error("Не удалось скопировать TgId:", err);
			setError("Не удалось скопировать TgId.");
		}
	};
	const normalizeReports = (data) => {
		const list = Array.isArray(data) ? data : [];

		return list
			.map((group) => {
				const reports = Array.isArray(group.reports) ? group.reports : [];

				const normalizedReports = reports.map((report) => ({
					reportId: report.reportId,
					fromUserId: report.fromUserId ?? 0,
					fromTgId: report.fromTgId ?? 0,
					fromUserUsername: report.fromUserUsername ?? "",
					fromUserFullName: report.fromUserFullName ?? "",
					fromUserMainPhoto: report.fromUserMainPhoto ?? "",
					report: report.report ?? "",
					cDate: report.cDate,
					isViewed: report.isViewed === true,
					userId: group.userId,
				}));

				return {
					userId: group.userId ?? 0,
					tgId: group.tgId ?? 0,
					userUsername: group.userUsername ?? "",
					userFullName: group.userFullName ?? "",
					userMainPhoto: group.userMainPhoto ?? "",
					isBlocked: group.isBlocked === true,
					reports: normalizedReports,
					newReportsCount: normalizedReports.filter((r) => !r.isViewed).length,
					totalReportsCount: normalizedReports.length,
				};
			})
			.sort((a, b) => {
				if (b.newReportsCount !== a.newReportsCount) {
					return b.newReportsCount - a.newReportsCount;
				}

				const aDate = a.reports?.[0]?.cDate
					? new Date(a.reports[0].cDate).getTime()
					: 0;
				const bDate = b.reports?.[0]?.cDate
					? new Date(b.reports[0].cDate).getTime()
					: 0;

				return bDate - aDate;
			});
	};

	const loadReports = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const data = await getReports();
			const normalizedData = normalizeReports(data);

			setGroupedReports(normalizedData);
		} catch (err) {
			console.error("Не удалось загрузить жалобы:", err);
			setError("Не удалось загрузить жалобы. Попробуйте ещё раз.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadReports();
	}, [loadReports]);

	const handleSelectUser = (group) => {
		setSelectedUserId(group.userId);
	};

	const handleCloseDetails = useCallback(() => {
		setSelectedUserId(null);
	}, []);

	useEffect(() => {
		if (!selectedUserId) return undefined;

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				handleCloseDetails();
			}
		};

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = previousOverflow;
		};
	}, [selectedUserId, handleCloseDetails]);

	const handleMarkViewed = async (reportId, userId) => {
		setProcessing((prev) => ({ ...prev, [`view-${reportId}`]: true }));

		try {
			await markReportViewed(reportId);

			setGroupedReports((prev) =>
				prev.map((group) => {
					if (group.userId !== userId) return group;

					const reports = group.reports.map((report) =>
						report.reportId === reportId
							? { ...report, isViewed: true }
							: report,
					);

					return {
						...group,
						reports,
						newReportsCount: reports.filter((r) => !r.isViewed).length,
						totalReportsCount: reports.length,
					};
				}),
			);
		} catch (err) {
			console.error("Не удалось отметить жалобу просмотренной:", err);
			setError("Не удалось отметить жалобу просмотренной.");
		} finally {
			setProcessing((prev) => ({ ...prev, [`view-${reportId}`]: false }));
		}
	};

	const handleBlockUser = async (userId) => {
		setProcessing((prev) => ({ ...prev, [`block-${userId}`]: true }));

		try {
			const success = await blockUser({
				userId,
				reason: "Жалобы",
			});

			if (success) {
				setGroupedReports((prev) =>
					prev.map((group) =>
						group.userId === userId ? { ...group, isBlocked: true } : group,
					),
				);
			}
		} catch (err) {
			console.error(`Не удалось заблокировать пользователя ${userId}:`, err);
			setError("Не удалось заблокировать пользователя.");
		} finally {
			setProcessing((prev) => ({ ...prev, [`block-${userId}`]: false }));
		}
	};

	const handleUnBlockUser = async (userId) => {
		setProcessing((prev) => ({ ...prev, [`unblock-${userId}`]: true }));

		try {
			const success = await unBlockUser(userId);

			if (success) {
				setGroupedReports((prev) =>
					prev.map((group) =>
						group.userId === userId ? { ...group, isBlocked: false } : group,
					),
				);
			}
		} catch (err) {
			console.error(`Не удалось разблокировать пользователя ${userId}:`, err);
			setError("Не удалось разблокировать пользователя.");
		} finally {
			setProcessing((prev) => ({ ...prev, [`unblock-${userId}`]: false }));
		}
	};

	const renderUserCell = ({
		userId,
		tgId,
		userUsername,
		userFullName,
		userMainPhoto,
	}) => {
		const photoSrc = getPhotoSrc(userId, userMainPhoto);
		const name = userFullName || "-";

		return (
			<div className="user-cell">
				<img src={photoSrc} alt={name} className="user-photo" />

				<span>
					<strong>{name}</strong>
					{userUsername && (
						<>
							<br />
							<a
								href={`https://t.me/${userUsername}`}
								target="_blank"
								rel="noopener noreferrer"
								className="user-telegram-link"
								onClick={(event) => event.stopPropagation()}
							>
								@{userUsername}
							</a>
						</>
					)}
					<br />
					ID: {userId}
					{tgId ? (
						<>
							<br />
							<button
								type="button"
								className="tgid-copy"
								title="Скопировать TgId"
								onClick={(event) => copyToClipboard(tgId, event)}
							>
								TgId: {tgId}
							</button>
						</>
					) : null}
				</span>
			</div>
		);
	};

	const renderActionCell = (group) => {
		const { userId, isBlocked, reports } = group;
		const allViewed =
			Array.isArray(reports) && reports.every((r) => r.isViewed);

		if (isBlocked) {
			return (
				<button
					type="button"
					className="unblock-button"
					onClick={() => handleUnBlockUser(userId)}
					disabled={processing[`unblock-${userId}`]}
				>
					{processing[`unblock-${userId}`]
						? "Разблокируем..."
						: "Разблокировать"}
				</button>
			);
		}

		return (
			<button
				type="button"
				className="block-button"
				onClick={() => handleBlockUser(userId)}
				disabled={!allViewed || processing[`block-${userId}`]}
				title={!allViewed ? "Сначала просмотрите все жалобы" : undefined}
			>
				{processing[`block-${userId}`] ? "Блокируем..." : "Заблокировать"}
			</button>
		);
	};

	if (loading) {
		return <div className="reports-container">Загружаем жалобы...</div>;
	}

	const allReportsCount = groupedReports.reduce(
		(sum, group) => sum + (group.totalReportsCount || 0),
		0,
	);

	const newReportsCount = groupedReports.reduce(
		(sum, group) => sum + (group.newReportsCount || 0),
		0,
	);

	return (
		<div className="reports-container">
			<h2>Жалобы</h2>

			{error && <div className="reports-error">{error}</div>}

			<div className="report-counts">
				<span>Всего: {allReportsCount}</span>
				<span>Новые: {newReportsCount}</span>
			</div>

			<div className="reports-table-wrap">
				<table className="reports-table">
					<thead>
						<tr>
							<th>ID пользователя</th>
							<th>Пользователь</th>
							<th>Новые</th>
							<th>Всего</th>
							<th>Действие</th>
						</tr>
					</thead>
					<tbody>
						{groupedReports.length === 0 ? (
							<tr>
								<td colSpan={5} className="empty-cell">
									Жалобы не найдены
								</td>
							</tr>
						) : (
							groupedReports.map((group) => (
								<tr
									key={group.userId}
									className={[
										group.isBlocked ? "blocked" : "",
										group.userId === selectedUserId ? "selected" : "",
									]
										.filter(Boolean)
										.join(" ")}
									onClick={() => handleSelectUser(group)}
								>
									<td>{group.userId}</td>
									<td>{renderUserCell(group)}</td>
									<td>
										<span
											className={
												group.newReportsCount > 0
													? "count-pill count-pill-new"
													: "count-pill"
											}
										>
											{group.newReportsCount}
										</span>
									</td>
									<td>{group.totalReportsCount}</td>
									<td
										className="action-buttons"
										onClick={(event) => event.stopPropagation()}
									>
										{renderActionCell(group)}
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{selectedGroup && selectedUserReports && (
				<div
					className="reports-modal-backdrop"
					role="presentation"
					onClick={handleCloseDetails}
				>
					<div
						className="reports-modal"
						role="dialog"
						aria-modal="true"
						aria-label="Детали жалоб"
						onClick={(event) => event.stopPropagation()}
					>
						<div className="reports-modal-header">
							<div className="reports-modal-title">
								<h3>Детали жалоб</h3>
								<div className="reports-modal-user">
									{renderUserCell(selectedGroup)}
								</div>
								<div className="reports-modal-meta">
									<span className="count-pill count-pill-new">
										Новые: {selectedGroup.newReportsCount}
									</span>
									<span className="count-pill">
										Всего: {selectedGroup.totalReportsCount}
									</span>
									{selectedGroup.isBlocked && (
										<span className="count-pill count-pill-blocked">
											Заблокирован
										</span>
									)}
								</div>
							</div>

							<div className="reports-modal-actions">
								<div className="action-buttons reports-modal-action-cell">
									{renderActionCell(selectedGroup)}
								</div>
								<button
									type="button"
									className="reports-modal-close"
									aria-label="Закрыть"
									onClick={handleCloseDetails}
								>
									×
								</button>
							</div>
						</div>

						<div className="reports-modal-body">
							<div className="reports-table-wrap">
								<table className="reports-table details-table">
									<thead>
										<tr>
											<th>ID</th>
											<th>Кто пожаловался</th>
											<th>Текст</th>
											<th>Дата</th>
											<th>Действие</th>
										</tr>
									</thead>
									<tbody>
										{selectedUserReports.length === 0 ? (
											<tr>
												<td colSpan={5} className="empty-cell">
													Детали не найдены
												</td>
											</tr>
										) : (
											selectedUserReports.map((report) => (
												<tr
													key={report.reportId}
													className={report.isViewed ? "viewed" : ""}
												>
													<td>{report.reportId}</td>
													<td>
														{renderUserCell({
															userId: report.fromUserId,
															tgId: report.fromTgId,
															userUsername: report.fromUserUsername,
															userFullName: report.fromUserFullName,
															userMainPhoto: report.fromUserMainPhoto,
														})}
													</td>
													<td className="report-text">
														{report.report || "-"}
													</td>
													<td>{formatDate(report.cDate)}</td>
													<td className="action-buttons">
														<button
															type="button"
															className="view-button"
															onClick={() =>
																handleMarkViewed(
																	report.reportId,
																	report.userId,
																)
															}
															disabled={
																report.isViewed ||
																processing[`view-${report.reportId}`]
															}
														>
															{report.isViewed
																? "Просмотрено"
																: processing[`view-${report.reportId}`]
																	? "Сохраняем..."
																	: "Отметить просмотренной"}
														</button>
													</td>
												</tr>
											))
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Reports;
